import { FinanceCategoryScope, type MoneyText } from "@/features/finance/model/finance-types"

/** Re-exported so the accounting surfaces can name the money type without reaching across to finance-types. */
export type { MoneyText }

/**
 * Types for the read-only Accounting reports (ADR-0021 tasks 20–22): chart of accounts, journal, general
 * ledger, trial balance, and financial statements. All are derived from the double-entry journal on the API.
 */

export enum AccountingAccountType {
  Asset = "ASSET",
  Liability = "LIABILITY",
  NetAsset = "NET_ASSET",
  Income = "INCOME",
  Expense = "EXPENSE",
}

/**
 * The finer-grained layer beneath `AccountingAccountType` (ADR-0021). Classification is the source of
 * truth on the API — `type` and `normalBalance` are derived from it — which is what allows a contra-asset
 * (ACCUMULATED_DEPRECIATION: an ASSET with a CREDIT normal balance) to exist.
 *
 * Accounting-facing only. Churches never see this; it appears on the Chart of Accounts, which already
 * speaks accounting.
 */
export enum AccountingAccountClassification {
  CashAndBank = "CASH_AND_BANK",
  CurrentAsset = "CURRENT_ASSET",
  Receivable = "RECEIVABLE",
  Inventory = "INVENTORY",
  FixedAsset = "FIXED_ASSET",
  AccumulatedDepreciation = "ACCUMULATED_DEPRECIATION",
  CurrentLiability = "CURRENT_LIABILITY",
  LongTermLiability = "LONG_TERM_LIABILITY",
  NetAssets = "NET_ASSETS",
  Revenue = "REVENUE",
  OtherIncome = "OTHER_INCOME",
  OperatingExpense = "OPERATING_EXPENSE",
  DepreciationExpense = "DEPRECIATION_EXPENSE",
  OtherExpense = "OTHER_EXPENSE",
}

/**
 * Classifications that sit on the OPPOSITE side to their type's natural balance. Mirrors
 * `CONTRA_CLASSIFICATIONS` in the API's chart-of-accounts service — keep the two in step.
 */
export const CONTRA_CLASSIFICATIONS: readonly AccountingAccountClassification[] = [
  AccountingAccountClassification.AccumulatedDepreciation,
]

export enum NormalBalance {
  Debit = "DEBIT",
  Credit = "CREDIT",
}

export enum JournalLineDirection {
  Debit = "DEBIT",
  Credit = "CREDIT",
}

export enum JournalEntryStatus {
  Posted = "POSTED",
  Reversed = "REVERSED",
}

export type AccountingScopeQuery = {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
}

export type AccountingChartQuery = AccountingScopeQuery & {
  dateTo?: string
  type?: AccountingAccountType
}

export type AccountingJournalQuery = AccountingScopeQuery & {
  dateFrom?: string
  dateTo?: string
  /** Free-text filter: matches a transaction reference, or an account code/name on any line. */
  search?: string
  page?: number
  limit?: number
}

export type AccountingLedgerQuery = AccountingScopeQuery & {
  accountingAccountId: string
  dateFrom?: string
  dateTo?: string
  fundId?: string | null
}

export type AccountingPeriodQuery = AccountingScopeQuery & {
  dateFrom?: string
  dateTo?: string
}

export type ChartAccount = {
  id: string
  code: string
  /** Display name, already in the reader's language: the API localizes seeded accounts (ADR-0030). */
  name: string
  /** Seeded-name key; informational only, never needed for display. Null for a CUSTOM account. */
  translationKey: string | null
  /** A seeded account's description, localized by the API; null for CUSTOM / SYSTEM accounts. */
  description?: string | null
  type: AccountingAccountType
  classification: AccountingAccountClassification
  normalBalance: NormalBalance
  status: string
  balance: MoneyText
}

export type ChartOfAccountsResponse = { items: ChartAccount[] }

/** The kind of income/expense account a user can create directly in the Chart of Accounts (categories→accounts). */
export enum ChartAccountKind {
  Income = "INCOME",
  Expense = "EXPENSE",
}

export type CreateAccountingAccountInput = {
  workspaceId: string
  kind: ChartAccountKind
  name: string
  /**
   * Which branch OWNS the account, or absent for a workspace-wide one (ADR-0024 Phase 4).
   *
   * Only meaningful under BRANCH_AUTONOMY; the server refuses it otherwise rather than quietly creating a
   * global account, because a caller that meant to keep something to itself would have published it.
   */
  owningBranchId?: string | null
}

export type CreatedAccountingAccount = { id: string; code: string; name: string }

export type JournalLineView = {
  accountCode: string
  accountName: string
  accountTranslationKey?: string | null
  accountType: AccountingAccountType
  direction: JournalLineDirection
  amount: MoneyText
  fundId: string | null
  financeAccountId: string | null
}

export type JournalEntryView = {
  id: string
  entryDate: string
  status: JournalEntryStatus
  sourceType: string
  currency: string
  transactionReference: string | null
  totalDebit: MoneyText
  totalCredit: MoneyText
  lines: JournalLineView[]
}

export type JournalResponse = {
  items: JournalEntryView[]
  total: number
  page: number
  limit: number
}

export type LedgerLineView = {
  entryDate: string
  direction: JournalLineDirection
  amount: MoneyText
  runningBalance: MoneyText
  status: JournalEntryStatus
  sourceType: string
  /** The transaction this line came from — null for entries with no transaction (an opening balance). */
  transactionId: string | null
  transactionReference: string | null
  fundId: string | null
}

export type LedgerResponse = {
  account: {
    id: string
    code: string
    name: string
    translationKey?: string | null
    type: AccountingAccountType
    normalBalance: NormalBalance
    status: string
  }
  openingBalance: MoneyText
  closingBalance: MoneyText
  lines: LedgerLineView[]
}

export type TrialBalanceRow = {
  id: string
  code: string
  name: string
  translationKey?: string | null
  type: AccountingAccountType
  debit: MoneyText
  credit: MoneyText
  balance: MoneyText
}

export type TrialBalanceResponse = {
  rows: TrialBalanceRow[]
  totalDebit: MoneyText
  totalCredit: MoneyText
  balanced: boolean
}

export type StatementLineItem = { code: string; name: string; translationKey?: string | null; amount: MoneyText }
export type StatementSection = { items: StatementLineItem[]; total: MoneyText }

export type StatementsResponse = {
  period: { dateFrom: string | null; dateTo: string | null }
  balanceSheet: {
    assets: StatementSection
    liabilities: StatementSection
    netAssets: StatementSection & { accumulatedSurplus: MoneyText }
    balanced: boolean
  }
  incomeStatement: {
    income: StatementSection
    expenses: StatementSection
    surplus: MoneyText
  }
  cashFlow: {
    openingCash: MoneyText
    inflows: MoneyText
    outflows: MoneyText
    netChange: MoneyText
    closingCash: MoneyText
  }
}
